import { Observable, firstValueFrom, from } from 'rxjs';
import { map } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { ScrAuthenticationService } from './authentication.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ShortCodesRole } from '../models/constants';

/**
 * Type definition for the JWT after decode.
 */
export interface DecodedToken {
  sub: string;
  programType: string;  // inserted by SCR - e.g. Standard
  iss: string;
  "custom:accountId": string;  // inserted by SCR
  "cognito:username": string;
  "cognito:groups": string[];  // short names (e.g. ServiceDesk)
  "cognito:roles": string[];   // full cognito role name ARNs
  userId: string; // short codes string version of user id value.
  email: string;

  /**
   * UUID.  When you revoke a token, Amazon Cognito invalidates all access and ID tokens with the same origin_jti value.
   */
  origin_jti: string;

  /**
   * The user pool app client that authenticated your user.
   */
  aud: string;
  event_id: string;

  /**
   * The intended purpose of the token. In an ID token, its value is id.
   */
  token_use: string;

  /**
   * The authentication time, in Unix time format, that your user completed authentication.
   */
  auth_time: number;

  /**
   * The expiration time, in Unix time format, that your user's token expires.
   */
  exp: number;

  /**
   * The issued-at time, in Unix time format, that Amazon Cognito issued your user's token.
   */
  iat: number;

  /**
   * The unique identifier of the JWT - a UUID.
   */
  jti: string;
}

@Injectable({
  providedIn: 'root'
})
export class ScrAuthorizationService {

  public static Registrant: number = Object.keys(ShortCodesRole).indexOf(ShortCodesRole.Registrant);
  public static RegistrantAdmin = Object.keys(ShortCodesRole).indexOf(ShortCodesRole.RegistrantAdmin);
  public static Carrier = Object.keys(ShortCodesRole).indexOf(ShortCodesRole.Carrier);
  public static CarrierRegistrant = Object.keys(ShortCodesRole).indexOf(ShortCodesRole.CarrierRegistrant);
  public static ServiceDesk = Object.keys(ShortCodesRole).indexOf(ShortCodesRole.ServiceDesk);
  public static SuperServiceDesk = Object.keys(ShortCodesRole).indexOf(ShortCodesRole.SuperAdmin);
  public static ServiceDeskOperations = Object.keys(ShortCodesRole).indexOf(ShortCodesRole.ServiceDeskOperations);
  public static BIReports = Object.keys(ShortCodesRole).indexOf(ShortCodesRole.BIReports);
  public static CTIAAdmin = Object.keys(ShortCodesRole).indexOf(ShortCodesRole.CTIAAdmin);
  public static VettingMonitoring = Object.keys(ShortCodesRole).indexOf(ShortCodesRole.VettingMonitoring);

  private jwtHelper = new JwtHelperService();

  constructor(
    private authenticationService: ScrAuthenticationService,
    private router: Router,
    private logger: NGXLogger) {
  }

  hasRole(groupName: string | string[]): Observable<boolean> {
    return from(this.authenticationService.getIdToken()).pipe(map(tok => {
      if (!tok) {
        return false;
      }

      const decoded = this.jwtHelper.decodeToken<DecodedToken>(tok);
      if (!decoded) {
        return false;
      }
      const groups = decoded['cognito:groups'] as string[];
      if (groups && groups.length) {
        if (typeof groupName === 'string') {
          return groups.includes(groupName);
        }
        else {
          // do any of the user's groups match any of these ids.
          const rids = groupName as string[];
          for (const id of rids) {
            if (groups.includes(id)) {
              return true;
            }
          }
          return false;
        }
      }
      else {
        return false;
      }
    }));
  }

  getAuthorizedGroups(): Observable<string[]> {
    return from(this.authenticationService.getIdToken(false)).pipe(map(tok => {
      if (!tok) {
        return [];
      }
      const decoded = this.jwtHelper.decodeToken<DecodedToken>(tok);
      if (!decoded) {
        return [];
      }
      const groups = decoded['cognito:groups'] as string[];
      if (groups) {
        return groups;
      }
      else {
        return [];
      }
    }));
  }

  unauthorized() {
    this.logger.trace('routing user to unauthorized page since they are logged in but unable to access the requested page.');
    this.router.navigate(['dashboard', { outlets: { 'navbar-tab': ['unauthorized'] } }]);
  }


  async routeUserPostLogin(cartCount: number): Promise<boolean> {

    const groups = await firstValueFrom(this.getAuthorizedGroups());

    if (groups.includes(ShortCodesRole.Registrant) || groups.includes(ShortCodesRole.CarrierRegistrant)) {
      if (cartCount > 0)
        return this.router.navigate(['dashboard', { outlets: { 'navbar-tab': ['order-code', { outlets: { 'order-code-tab': ['cart'] } }] } }]);
      else
        return this.router.navigate(['dashboard', { outlets: { 'navbar-tab': ['my-shortcodes', { outlets: { 'my-shortcode-tab': ['shortcode-list'] } }] } }]);
    }
    else if (groups.includes(ShortCodesRole.Carrier)) {
      if (cartCount > 0)
        return this.router.navigate(['dashboard', { outlets: { 'navbar-tab': ['order-code', { outlets: { 'order-code-tab': ['cart'] } }] } }]);
      else
        return this.router.navigate(['dashboard', { outlets: { 'navbar-tab': ['code-management', { outlets: { 'code-management-tab': ['code-lookup'] } }] } }]);
    }
    else if (groups.includes(ShortCodesRole.ServiceDesk)) {
      this.router.navigate(['dashboard', { outlets: { 'navbar-tab': ['account-search'] } }])
        .catch(err => {
          this.logger.warn('error in nav for SD', err);
          this.router.navigate(['dashboard', { outlets: { 'navbar-tab': ['unauthorized'] } }]);
        });
      return true;
    }
    else {
      if (groups.length > 0) {
        this.logger.warn('nowhere to route to in routeUserPostLogin.  ' + JSON.stringify(groups));
      }
      return false;
    }

  }

  public async basePathRedirector(): Promise<boolean> {
    this.logger.debug('base path redirector called.');
    const signedIn = await this.authenticationService.userIsSignedIn();
    if (signedIn) {
      const b = await this.routeUserPostLogin(0);
      return b;
    }
    else {
      this.router.navigate(['/sec/login']);
      return false;
    }
  }
}

